import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Phone, Flame } from "lucide-react";

const DessertPotIcon = () => (
  <svg
    data-testid="dessert-pot-icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-terra/40"
  >
    <path d="M5 11h12" />
    <path d="M6 11l1.1 8.3a2 2 0 0 0 2 1.7h3.8a2 2 0 0 0 2-1.7L16 11" />
    <path d="M8 11a3.5 3.5 0 0 1 7 0" />
    <path d="M13 9.5 17.5 3.2" />
    <circle cx="18.3" cy="2.4" r="1.1" />
  </svg>
);

const CannoliIcon = () => (
  <svg
    data-testid="cannoli-icon"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-terra/40"
  >
    <path d="M4.2 12.8 C7.2 9.3 14.2 6.8 17.6 7.3 C20 7.7 20.6 10.6 18.7 12.2 C15.4 15 8.4 17.6 5.2 17.1 C2.9 16.7 2.6 14.7 4.2 12.8 Z" />
    <ellipse cx="4.9" cy="14.9" rx="1.6" ry="2.3" transform="rotate(-18 4.9 14.9)" />
    <ellipse cx="18.2" cy="9.8" rx="1.6" ry="2.3" transform="rotate(-18 18.2 9.8)" />
    <path d="M9.2 12.6l1.4-.6" />
    <path d="M12.8 11.6l1.4-.6" />
  </svg>
);

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  className: "text-terra/40",
};

const CanIcon = () => (
  <svg data-testid="icon-can" {...iconProps}>
    <path d="M10 3h4" />
    <path d="M10 3 8.8 6" />
    <path d="M14 3l1.2 3" />
    <path d="M8.8 6h6.4" />
    <path d="M8.8 6v13a2 2 0 0 0 2 2h2.4a2 2 0 0 0 2-2V6" />
    <path d="M8.8 16.5h6.4" />
  </svg>
);

const PlasticBottleIcon = () => (
  <svg data-testid="icon-plastic-bottle" {...iconProps}>
    <path d="M10.6 2h2.8v1.6h-2.8z" />
    <path d="M11.2 3.6v1.2" />
    <path d="M12.8 3.6v1.2" />
    <path d="M11.2 4.8c0 .8-2.6 1.6-2.6 3.4v9.3a2.3 2.3 0 0 0 2.3 2.3h2.2a2.3 2.3 0 0 0 2.3-2.3V8.2c0-1.8-2.6-2.6-2.6-3.4" />
    <path d="M8.6 11h6.8" />
    <path d="M8.6 14.5h6.8" />
  </svg>
);

const GlassBottleIcon = () => (
  <svg data-testid="icon-glass-bottle" {...iconProps}>
    <path d="M10.5 2h3v2h-3z" />
    <path d="M11 4v2.2c0 .9-2.3 1.8-2.3 3.8v8a2.5 2.5 0 0 0 2.5 2.5h1.6a2.5 2.5 0 0 0 2.5-2.5v-8c0-2-2.3-2.9-2.3-3.8V4" />
    <path d="M8.7 13.5h6.6" />
    <path d="M8.7 16.5h6.6" />
  </svg>
);

const BeerBottleIcon = () => (
  <svg data-testid="icon-beer-bottle" {...iconProps}>
    <path d="M10.4 4.5V3.4l.55-1 .6 1 .6-1 .6 1 .6-1 .55 1v1.1" />
    <path d="M11.1 4.5v1.7c0 .9-2.3 1.8-2.3 3.8v8a2.5 2.5 0 0 0 2.5 2.5h1.4a2.5 2.5 0 0 0 2.5-2.5v-8c0-2-2.3-2.9-2.3-3.8V4.5" />
    <path d="M8.8 13.5h6.4" />
    <path d="M8.8 16.5h6.4" />
  </svg>
);

const WineBottleIcon = () => (
  <svg data-testid="icon-wine-bottle" {...iconProps}>
    <path d="M11 2h2" />
    <path d="M11 2.5v5.3c0 1-3 2.2-3 4.7v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-7c0-2.5-3-3.7-3-4.7V2.5" />
    <path d="M8 14.5h8" />
    <path d="M8 17.5h8" />
  </svg>
);

function DrinkIcon({ name }) {
  if (name.startsWith("Sodas")) return <CanIcon />;
  if (name.startsWith("Bières")) return <BeerBottleIcon />;
  if (name.startsWith("Eau")) return <PlasticBottleIcon />;
  if (name.startsWith("Vins")) return <WineBottleIcon />;
  if (name.startsWith("Limonades")) return <GlassBottleIcon />;
  return <Flame size={20} className="text-terra/40" />;
}
import api from "@/lib/api";
import { PHONE_TEL, scrollToSection } from "@/lib/hours";

const BADGES = {
  vegetarien: { label: "Végétarienne", cls: "badge-veg" },
  epice: { label: "Épicée", cls: "badge-hot" },
  coup_de_coeur: { label: "Coup de cœur", cls: "badge-fav" },
};

const fmtPrice = (p) => (p > 0 ? `${p.toFixed(2).replace(".", ",")} €` : "€");

const PIZZA_CATS = ["Nos Pizzas Rouges", "Nos Pizzas Blanches", "Calabria Style"];

const FAMILIES = [
  { key: "Nos Pizzas Rouges", subtitle: "Base sauce tomate", id: "famille-rouges" },
  { key: "Nos Pizzas Blanches", subtitle: "Base crème fraîche", id: "famille-blanches" },
  { key: "Calabria Style", subtitle: "Charcuteries & fromages calabrais", id: "famille-calabria" },
  { key: "Desserts", subtitle: "Pour finir en douceur", id: "famille-desserts" },
  { key: "Boissons", subtitle: "Pour accompagner", id: "famille-boissons" },
];

function ItemCard({ p, i, onSelect }) {
  return (
    <motion.button
      data-testid={`pizza-card-${p.id}`}
      onClick={() => onSelect(p)}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
      className={`group overflow-hidden rounded-2xl border border-border/60 bg-surface-2 text-left transition-colors hover:border-terra/50 ${!p.available ? "opacity-50" : ""}`}
    >
      {p.image ? (
        <div className="relative h-44 overflow-hidden">
          <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <span className="font-display absolute bottom-3 right-3 rounded-full bg-background/80 px-3.5 py-1 text-sm font-bold text-ochre backdrop-blur-md" data-testid={`pizza-price-${p.id}`}>
            {fmtPrice(p.price)}
          </span>
        </div>
      ) : (
        <div className="flex h-24 items-end justify-between bg-surface-3 px-5 pb-3">
          {p.name.startsWith("Tiramisu") ? (
            <DessertPotIcon />
          ) : p.name.startsWith("Mini Cannoli") ? (
            <CannoliIcon />
          ) : p.category === "Boissons" ? (
            <DrinkIcon name={p.name} />
          ) : (
            <Flame size={20} className="text-terra/40" />
          )}
          <span className="font-display text-sm font-bold text-ochre" data-testid={`pizza-price-${p.id}`}>{fmtPrice(p.price)}</span>
        </div>
      )}
      <div className="p-5">
        <h3 className="font-display text-lg font-bold" data-testid={`pizza-title-${p.id}`}>
          {p.name}
          {!p.available && <span className="ml-2 font-code text-[10px] uppercase tracking-widest text-dim">Épuisée</span>}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm text-dim">{p.ingredients.join(" · ")}</p>
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          {p.badges.map((b) => BADGES[b] && <span key={b} className={`badge-tag ${BADGES[b].cls}`}>{BADGES[b].label}</span>)}
        </div>
      </div>
    </motion.button>
  );
}

function FamilyBlock({ family, items, onSelect }) {
  if (items.length === 0) return null;
  return (
    <div id={family.id} data-testid={`${family.id}-block`} className="mt-16 scroll-mt-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55 }}
        className="flex flex-wrap items-baseline gap-x-4 gap-y-1"
      >
        <h3 className="font-display text-2xl font-bold sm:text-3xl">{family.key}</h3>
        <span className="font-editorial text-lg italic text-terra">{family.subtitle}</span>
      </motion.div>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p, i) => (
          <ItemCard key={p.id} p={p} i={i} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

export default function Carte() {
  const [pizzas, setPizzas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [badgeFilter, setBadgeFilter] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/pizzas")
      .then((r) => setPizzas(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const formules = pizzas.filter((p) => p.category === "Les Formules Midi");
  const filtering = query.trim() !== "" || badgeFilter !== null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pizzas.filter((p) => {
      if (badgeFilter && !p.badges.includes(badgeFilter)) return false;
      if (q && !(`${p.name} ${p.description} ${p.ingredients.join(" ")}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [pizzas, query, badgeFilter]);

  return (
    <section id="carte" data-testid="carte-section" className="border-y border-border/60 bg-surface-1 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="overline-label">La carte</p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Nos pizzas, <span className="font-editorial italic text-terra">cuites à la minute.</span>
            </h2>
            <div className="relative w-full max-w-xs">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dim" />
              <input
                data-testid="carte-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une pizza, un ingrédient…"
                className="admin-input !rounded-full !pl-10"
              />
            </div>
          </div>

          <div data-testid="carte-category-tabs" className="mt-8 flex flex-wrap gap-2">
            {[...FAMILIES.slice(0, 3), { key: "Les Formules Midi", id: "famille-formules" }, ...FAMILIES.slice(3)].map((f) => (
              <button
                key={f.key}
                data-testid={`carte-tab-${f.key}`}
                onClick={() => scrollToSection(f.id)}
                className="rounded-full border border-border px-4 py-2 text-sm font-medium text-dim transition-colors hover:border-terra hover:text-terra"
              >
                {f.key}
              </button>
            ))}
            <span className="mx-1 hidden w-px bg-border sm:block" />
            {Object.entries(BADGES).map(([key, b]) => (
              <button
                key={key}
                data-testid={`carte-filter-${key}`}
                onClick={() => setBadgeFilter(badgeFilter === key ? null : key)}
                className={`badge-tag transition-opacity ${b.cls} ${badgeFilter === key ? "ring-2 ring-current" : "opacity-60 hover:opacity-100"}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </motion.div>

        <p className="mt-5 font-code text-[11px] uppercase leading-relaxed tracking-[0.15em] text-dim" data-testid="carte-supplements-note">
          Suppléments — Charcuterie · Viande · Poisson : 3,00 € / Garnitures · Fromages · Légumes : 2,00 € / Œuf frais : 1,00 €
        </p>

        {loading && <p className="mt-16 text-center text-dim" data-testid="carte-loading">La carte arrive du four…</p>}
        {error && !loading && (
          <p className="mt-16 text-center text-dim" data-testid="carte-error">
            Impossible de charger la carte pour le moment. Appelez-nous, on vous la lit au téléphone.
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p className="mt-16 text-center text-dim" data-testid="carte-empty">Aucune pizza ne correspond à votre recherche.</p>
        )}

        {filtering ? (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <ItemCard key={p.id} p={p} i={i} onSelect={setSelected} />
            ))}
          </div>
        ) : (
          FAMILIES.map((f) => (
            <FamilyBlock key={f.key} family={f} items={pizzas.filter((p) => p.category === f.key)} onSelect={setSelected} />
          ))
        )}

        {formules.length > 0 && (
          <motion.div
            data-testid="formules-midi-block"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            id="famille-formules"
            className="mt-20 scroll-mt-28 rounded-3xl border-2 p-7 sm:p-10"
            style={{ borderColor: "var(--olive)", background: "color-mix(in srgb, var(--olive) 7%, var(--surface-2))" }}
          >
            <p className="font-code text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: "var(--olive)" }}>
              Du mardi au vendredi · le midi uniquement
            </p>
            <h3 className="font-display mt-3 text-2xl font-bold sm:text-3xl">Les Formules Midi</h3>
            <div className="mt-8 grid gap-5 sm:grid-cols-3">
              {formules.map((f) => (
                <div
                  key={f.id}
                  data-testid={`formule-card-${f.id}`}
                  className="rounded-2xl border border-border/60 bg-surface-1 p-6 text-center transition-transform duration-300 hover:-translate-y-1"
                >
                  <p className="font-display text-lg font-bold">{f.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-dim">{f.ingredients.join(" + ")}</p>
                  <p className="font-display mt-4 text-2xl font-bold text-terra" data-testid={`formule-price-${f.id}`}>{fmtPrice(f.price)}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="pizza-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg overflow-hidden rounded-t-3xl border border-border bg-surface-2 sm:rounded-3xl"
            >
              {selected.image && <img src={selected.image} alt={selected.name} className="h-52 w-full object-cover" />}
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-code text-[10px] uppercase tracking-[0.3em] text-terra">{selected.category}</p>
                    <h3 className="font-display mt-1 text-2xl font-bold">{selected.name}</h3>
                  </div>
                  <button data-testid="pizza-detail-close" onClick={() => setSelected(null)} className="rounded-full border border-border p-2 text-dim hover:text-terra hover:border-terra transition-colors" aria-label="Fermer">
                    <X size={16} />
                  </button>
                </div>
                <p className="font-editorial mt-3 text-lg italic text-dim">{selected.description}</p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {selected.ingredients.map((ing) => (
                    <li key={ing} className="rounded-full bg-surface-3 px-3 py-1 text-xs text-dim">{ing}</li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
                  <span className="font-display text-2xl font-bold text-ochre">{fmtPrice(selected.price)}</span>
                  <a data-testid={`pizza-order-btn-${selected.id}`} href={PHONE_TEL} className="btn-primary !py-2.5 !px-5 text-sm">
                    <Phone size={15} /> Commander celle-ci
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
